import {
  TellescopeModelFromName,
  useAuth,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQuery,
  useUsersMap,
} from "@tellescope/tanstack-query";
import { ReversePaginatedList, Spinner } from "@tellescope/ui";
import { MessageBubbleWithSecureAvatar } from "../MessageBubbleWithSecureAvatar";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatName } from "@tellescope/utils";
import { MessageCompositionFooter } from "../MessageCompositionFooter";
import { MentionDropdown } from "../MentionDropdown";
import { MentionsBadgeRow } from "../MentionsBadgeRow";
import { useMentions } from "../useMentions";

export interface TeamChatConversationProps {
  chatroomId: string;
  draftId?: string;
}

export const TeamChatConversation = ({ chatroomId, draftId }: TeamChatConversationProps) => {
  const { t } = useTranslation("team-chat");
  const { currentUser } = useAuth();
  const messagesQuery = useTellescopeInfiniteQuery({
    model: "chats",
    method: "getSome",
    request: {
      filter: {
        roomId: chatroomId,
      },
    },
    select: (data) =>
      data.pages.flat().reduce<{
        scheduledMessages: TellescopeModelFromName<"chats">[];
        draftMessages: TellescopeModelFromName<"chats">[];
        otherMessages: TellescopeModelFromName<"chats">[];
      }>(
        (acc, curr) => {
          if (curr.sendAt && curr.sendAt.getTime() > new Date().getTime()) {
            acc.scheduledMessages.push(curr);
          } else if (curr.isDraft) {
            acc.draftMessages.push(curr);
          } else {
            acc.otherMessages.push(curr);
          }
          return acc;
        },
        { scheduledMessages: [], draftMessages: [], otherMessages: [] }
      ),
  });

  const chatroomQuery = useTellescopeQuery({
    model: "chat_rooms",
    method: "getOne",
    request: chatroomId,
    enabled: !!chatroomId,
  });

  const updateChatroomMutation = useTellescopeMutation({
    model: "chat_rooms",
    method: "updateOne",
  });

  const mentions = useMentions();

  const [composerKey, setComposerKey] = useState(0);

  const onMutationSuccess = () => {
    setComposerKey((k) => k + 1);
    mentions.reset();
  };

  const sendMessageMutation = useTellescopeMutation({
    model: "chats",
    method: "createOne",
    onSuccess: onMutationSuccess,
  });

  const updateMessageMutation = useTellescopeMutation({
    model: "chats",
    method: "updateOne",
    onSuccess: onMutationSuccess,
  });

  const matchingDraft = useMemo(
    () => messagesQuery.data?.scheduledMessages.find((m) => m.id === draftId),
    [messagesQuery.data?.scheduledMessages, draftId]
  );

  const isEditingScheduled = useMemo(
    () => !!matchingDraft?.sendAt && matchingDraft.sendAt.getTime() > Date.now(),
    [matchingDraft]
  );

  const sendAtInPast = useMemo(
    () => !!matchingDraft?.sendAt && matchingDraft.sendAt.getTime() <= Date.now(),
    [matchingDraft]
  );

  const buildCreateParams = ({
    message,
    attachments,
  }: {
    message: string;
    attachments: { type: string; name?: string; secureName: string }[];
  }) => {
    const { mentions: mentionIds } = mentions.buildMentionPayload();
    const existingUserIds = chatroomQuery.data?.userIds ?? [];
    const newUserIds = mentionIds.filter((id) => !existingUserIds.includes(id));
    if (newUserIds.length > 0) {
      const existingAssignedTo = chatroomQuery.data?.assignedTo ?? [];
      const newAssignedTo = newUserIds.filter((id) => !existingAssignedTo.includes(id));
      updateChatroomMutation.mutate({
        id: chatroomId,
        userIds: [...existingUserIds, ...newUserIds],
        assignedTo: [...existingAssignedTo, ...newAssignedTo],
        _options: { replaceObjectFields: true },
      });
    }
    return {
      roomId: chatroomId,
      message,
      userId: currentUser.id,
      attachments: attachments.map(({ type, name, secureName }) => ({ type, name: name ?? "", secureName })),
      linkOpenTrackingIds: [],
      ...(mentionIds.length > 0 ? { mentions: mentionIds } : {}),
    };
  };

  const messagesToDisplay = useMemo(
    () => [...(messagesQuery.data?.scheduledMessages ?? []), ...(messagesQuery.data?.otherMessages ?? [])].reverse(),
    [messagesQuery.data?.otherMessages, messagesQuery.data?.scheduledMessages]
  );

  const userMap = useUsersMap();

  if (!messagesQuery.data || messagesQuery.isLoading) {
    return (
      <div className="flex-grow flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      {messagesToDisplay.length === 0 ? (
        <div className="flex-grow flex items-center justify-center">
          <p className="text-sm text-muted-foreground">{t("No messages in this channel")}</p>
        </div>
      ) : (
        <ReversePaginatedList
          className="flex-grow px-4 py-2"
          autoScrollToBottom
          onTopReached={() => messagesQuery.fetchNextPage()}
        >
          {messagesToDisplay.map((message) => {
            const user = userMap[message.senderId || message.creator];
            const isScheduled = message.sendAt && message.sendAt.getTime() > new Date().getTime();

            return (
              <MessageBubbleWithSecureAvatar
                key={message.id}
                hideAvatar={message.senderId === currentUser.id}
                isHighlighted={!!message.mentions?.includes(currentUser.id)}
                timestamp={
                  isScheduled
                    ? message.sendAt || message.timestamp || message.createdAt
                    : message.timestamp || message.createdAt
                }
                status={isScheduled ? { type: "scheduled" } : undefined}
                content={message.message}
                sender={
                  user
                    ? {
                        type: "user",
                        id: user.id,
                        name: user.id === currentUser.id ? t("You") : formatName(user),
                        avatar: user.avatar,
                      }
                    : {
                        type: "user",
                        id: "",
                        name: t("Unknown User"),
                      }
                }
                isOutbound={message.senderId === currentUser.id}
              />
            );
          })}
        </ReversePaginatedList>
      )}
      <div className="relative">
        {mentions.mentionState.isOpen && (
          <MentionDropdown
            query={mentions.mentionState.query}
            onSelect={(target) => {
              mentions.onMentionSelect(target, mentions.textareaRef.current?.value ?? "");
            }}
            onDismiss={mentions.onMentionDismiss}
          />
        )}
        <MentionsBadgeRow
          pendingMentions={mentions.pendingMentions}
          existingUserIds={chatroomQuery.data?.userIds ?? []}
          onRemove={mentions.onMentionRemove}
        />
        <MessageCompositionFooter
          key={composerKey}
          canSend={!sendMessageMutation.isPending && !updateMessageMutation.isPending}
          isUpdatePending={updateMessageMutation.isPending}
          isEditingScheduled={isEditingScheduled}
          sendAtInPast={sendAtInPast}
          initDate={matchingDraft?.sendAt instanceof Date ? matchingDraft.sendAt : undefined}
          scheduleDialogKey={
            matchingDraft?.sendAt instanceof Date ? matchingDraft.sendAt.toISOString() : "no-init-date"
          }
          initialMessage={matchingDraft?.message}
          ref={mentions.textareaRef}
          onMessageChange={mentions.handleMessageChange}
          onSubmit={(payload) => {
            sendMessageMutation.mutate(buildCreateParams(payload));
          }}
          onSchedule={(payload, sendAt) => {
            if (matchingDraft) {
              updateMessageMutation.mutate({ id: matchingDraft.id, sendAt, message: payload.message });
            } else {
              sendMessageMutation.mutate({ ...buildCreateParams(payload), sendAt });
            }
          }}
          onSendNow={({ message }) => {
            if (!matchingDraft) return;
            updateMessageMutation.mutate({ id: matchingDraft.id, message, sendAt: new Date() });
          }}
          onSaveScheduled={({ message }) => {
            if (!matchingDraft) return;
            updateMessageMutation.mutate({ id: matchingDraft.id, message });
          }}
          onReschedule={({ message }, sendAt) => {
            if (!matchingDraft) return;
            updateMessageMutation.mutate({ id: matchingDraft.id, sendAt, message });
          }}
        />
      </div>
    </>
  );
};
